import React from 'react';
import { GaugeIcon } from 'lucide-react';
import { BASE_RULES_SUMMARY, BONUS_CAP_NOTE, BONUS_MAIN_NOTE, TIER_LABEL_NOTE } from '../../data/statement';
import { Band } from '../../types/commission';
import { formatUsd } from '../../utils/format';
import { Card } from '../sf/Card';
import { RiskNote } from '../sf/RiskNote';
import { UnderReviewNote } from '../sf/UnderReviewNote';
import { BandBar } from './BandBar';
interface TierProgressProps {
  branch: string;
  volume: number;
  unitsThisMonth: number;
  baseBands: Band[];
  bonusBands: Band[];
}
const BASE_BPS = 125;
const CAP_BPS = 150;
export function TierProgress({
  branch,
  volume,
  unitsThisMonth,
  baseBands,
  bonusBands
}: TierProgressProps) {
  const volumeM = volume / 1000000;
  const baseBand = baseBands.find(band => volumeM >= band.from && (band.to === null || volumeM < band.to));
  const bonusBand = bonusBands.find(band => unitsThisMonth >= band.from && (band.to === null || unitsThisMonth < band.to));
  const rawBonus = Number.parseInt(bonusBand?.valueLabel.replace('+', '') ?? '0', 10) || 0;
  const cappedBonus = Math.min(rawBonus, CAP_BPS - BASE_BPS);
  return <Card title="Base BPS (branch + period volume) & monthly bonus" icon={GaugeIcon} iconBg="#0176D3" meta="LO Standard Plan v1 parity" data-id="element-1056" data-mp="2nibog9k32ohy">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2" data-id="element-1057" data-mp="2dlp55p7eo5i1">
        <section aria-label="Base BPS" data-id="element-1058" data-mp="2djchcveyv0x2">
          <p className="text-[13px] font-semibold" data-id="element-1059" data-mp="3b9qx0k2ge21n">
            Base: {baseBand?.valueLabel}{' '}
            <span className="font-normal text-sf-weak" data-id="element-1060" data-mp="369l3szgjc7hg">
              · {branch} · October volume {formatUsd(volume)} ≥ $1,500,000.00
            </span>
          </p>
          <p className="text-xs text-sf-weak" data-id="element-1061" data-mp="31cunqc4qdbw1">Standard LO base by branch and the employee’s total loan volume for the period: {BASE_RULES_SUMMARY}.</p>
          <div className="mt-2" data-id="element-1062" data-mp="1pwxl5l3yytgc">
            <BandBar bands={baseBands} value={volumeM} scaleMax={3} markerLabel={`October ${formatUsd(volume)}`} ariaLabel={`${branch} period volume ${formatUsd(volume)}, ${baseBand?.valueLabel} base`} data-id="element-1063" data-mp="1px5wv9ocvspy" />
          </div>
          <div className="mt-2 space-y-1.5" data-id="element-1064" data-mp="gwtr5onxckcd">
            <RiskNote data-id="element-1065" data-mp="2zodp3lvsj73" />
            <p className="text-[11px] text-sf-weak" data-id="element-1066" data-mp="2yd8czcsf3dv">{TIER_LABEL_NOTE}</p>
          </div>
        </section>
        <section aria-label="Monthly bonus" data-id="element-1067" data-mp="7wydnd6wcd96">
          <p className="text-[13px] font-semibold" data-id="element-1068" data-mp="3perbklg5mutp">
            Monthly Bonus: +{cappedBonus} bps{' '}
            <span className="font-normal text-sf-weak" data-id="element-1069" data-mp="3u57mnywi64cw">
              · {bonusBand?.valueLabel} capped to +{cappedBonus} · {unitsThisMonth} closed loans in October
            </span>
          </p>
          <p className="text-xs text-sf-weak" data-id="element-1070" data-mp="3ld5qwdxdpc8y">Driven by the count of closed loans in the month — not funded units, not volume — and applied to every loan this month.</p>
          <div className="mt-2" data-id="element-1071" data-mp="36gna8d6ie02g">
            <BandBar bands={bonusBands} value={unitsThisMonth} scaleMax={9} discrete markerLabel={`${unitsThisMonth} closed loans`} ariaLabel={`${unitsThisMonth} closed loans this month, ${bonusBand?.valueLabel} bonus capped to +${cappedBonus}`} data-id="element-1072" data-mp="3beow15vbkdhm" />
          </div>
          <p className="mt-2 text-xs text-sf-weak" data-id="element-1073" data-mp="31snlzl9ne93c">
            {unitsThisMonth} closed loans → {bonusBand?.valueLabel} capped to +{cappedBonus}, so base + bonus = {BASE_BPS + cappedBonus} bps on all {unitsThisMonth} October loans.
          </p>
          <p className="mt-1 text-[11px] text-sf-weak" data-id="element-1074" data-mp="36qp7sdygkmii">
            {BONUS_CAP_NOTE} {BONUS_MAIN_NOTE}
          </p>
        </section>
      </div>
      <div className="mt-4" data-id="element-1075" data-mp="3khc5c2shg6d0">
        <UnderReviewNote data-id="element-1076" data-mp="3kjoim2napsjq" />
      </div>
    </Card>;
}